import type { FeedbackSurveyQuestion } from '~kui/components/KUI/Unified/Completion/FeedbackSurvey/types';
import type { CompletionActivity } from '~kui/components/KUI/Unified/Completion/KUIUniCompletionProgress.vue';
import type { CompletionSkill } from '~kui/components/KUI/Unified/Completion/KUIUniCompletionSkills.vue';
import type { CompletionPrize, CompletionSpotlight, PrizeOutcome } from '~kui/components/KUI/Unified/Completion/types';
import type { AvatarIllustration } from '~kui/types/kui';
import type { ConsiderationColor } from '~kui/utils/considerationColor';
import type { Ref } from 'vue';

import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

export interface CompletionConsideration {
  areaId: string;
  areaLabel: string;
  color?: ConsiderationColor;
  beforeLevel: number;
  beforeProgress: number;
  afterLevel: number;
  afterProgress: number;
  illustration?: string;
}

/**
 * The overall clarity (consideration) score before/after a completion, surfaced as a "clarity
 * bonus" screen. Populated by the app only when a consideration area levelled up during this
 * completion — a null value means the step is skipped.
 */
export interface CompletionClarity {
  beforeScore: number;
  afterScore: number;
  illustration?: AvatarIllustration;
}

export interface UseCompletionStepsProps {
  activities: CompletionActivity[];
  badgeUrl: string | null;
  feedbackSurvey: FeedbackSurveyQuestion[] | null;
  isFullyCompleted: boolean;
  coinsEarned: number | null;
  skills: CompletionSkill[];
  prize: CompletionPrize | null;
  assetName: string | null;
  collectionName: string | null;
  isGhostUser: boolean;
  prizeOutcome: PrizeOutcome;
  considerations: CompletionConsideration[];
  clarity: CompletionClarity | null;
  spotlights?: CompletionSpotlight[];
  /**
   * Whether this completion is a re-do of an already-completed item, when the host knows.
   * Null falls back to the step-derived guess (no celebration steps at all) — which misreads
   * a rewards-suppressed first completion, whose zeroed coins were the only step.
   */
  isReCompletion?: boolean | null;
}

export type CompletionStepName = 'reCompletion' | 'progress' | 'spotlight' | 'feedback' | 'consideration' | 'clarity' | 'coins' | 'skills' | 'prize';

export const useCompletionSteps = (props: Ref<UseCompletionStepsProps>) => {
  const { t } = useI18n();

  const hasFeedbackSurvey = computed(
    () => props.value.isFullyCompleted && props.value.feedbackSurvey && props.value.feedbackSurvey.length > 0,
  );

  // Whether a spotlight has been earned is the host's call: a collection's only arrives once
  // the collection is finished, an activity's arrives with the activity itself.
  const hasSpotlight = computed(
    () => !!props.value.spotlights?.some(spotlight => spotlight.links.length > 0),
  );

  const celebrationSteps = computed(() => {
    const steps: CompletionStepName[] = [];
    if (props.value.activities.length > 0 && props.value.badgeUrl) {
      // The badge step carries the spotlight itself, after the badge has been unlocked.
      steps.push('progress');
    }
    else if (hasSpotlight.value) {
      // No badge to ride on: the spotlight takes the badge's slot and stands alone.
      steps.push('spotlight');
    }
    if (hasFeedbackSurvey.value) {
      steps.push('feedback');
    }
    if (props.value.considerations.length > 0) {
      steps.push('consideration');
    }
    if (props.value.clarity) {
      steps.push('clarity');
    }
    if (props.value.coinsEarned && props.value.coinsEarned > 0) {
      steps.push('coins');
    }
    if (props.value.skills.length > 0) {
      steps.push('skills');
    }
    if (props.value.prize && props.value.prize.isUserEligible) {
      steps.push('prize');
    }
    return steps;
  });

  const isReCompletion = computed(
    () => (props.value.isReCompletion ?? celebrationSteps.value.length === 0) && !!props.value.assetName,
  );

  // A re-do opens on its thumbs-up before anything else it still earns. With nothing else to show
  // it stays stepless, so the thumbs-up and Continue appear together without a tap.
  const activeSteps = computed((): CompletionStepName[] =>
    (isReCompletion.value && celebrationSteps.value.length > 0
      ? ['reCompletion', ...celebrationSteps.value]
      : celebrationSteps.value),
  );

  const step = ref(0);
  const totalSteps = computed(() => activeSteps.value.length);
  const isEmpty = computed(() => activeSteps.value.length === 0);
  const hasCoinsToSave = computed(() => props.value.coinsEarned && props.value.coinsEarned > 0);
  const showCta = computed(() => isEmpty.value || step.value > totalSteps.value);

  const currentStepName = computed((): CompletionStepName | null => {
    if (showCta.value) {
      return activeSteps.value[activeSteps.value.length - 1] ?? null;
    }
    return activeSteps.value[step.value - 1] ?? null;
  });

  const showTapOverlay = computed(
    () => !showCta.value && currentStepName.value !== 'feedback',
  );

  const completionHeading = computed(() => {
    if (!props.value.assetName) {
      return null;
    }
    const params = { assetName: props.value.assetName, collectionName: props.value.collectionName ?? '' };
    if (isReCompletion.value) {
      return props.value.collectionName
        ? t('unified.activity.completion.reCompletionHeadingCollection', params)
        : t('unified.activity.completion.reCompletionHeadingStandalone', params);
    }
    return props.value.collectionName
      ? t('unified.activity.completion.completionHeadingCollection', params)
      : t('unified.activity.completion.completionHeadingStandalone', params);
  });

  const ctaText = computed(() => {
    if (props.value.isGhostUser) {
      return t('unified.activity.completion.saveProgress');
    }
    return t('unified.activity.completion.continueJourney');
  });

  const handleTap = () => {
    if (currentStepName.value === 'feedback') {
      return;
    }
    if (step.value <= totalSteps.value) {
      step.value++;
    }
  };

  const advanceStep = () => {
    step.value++;
  };

  const init = () => {
    step.value = 1;
  };

  return {
    activeSteps,
    step,
    totalSteps,
    isEmpty,
    isReCompletion,
    hasCoinsToSave,
    showCta,
    currentStepName,
    showTapOverlay,
    completionHeading,
    ctaText,
    handleTap,
    advanceStep,
    init,
  };
};
